import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  ArrowRight,
  CheckCircle2,
  Network,
  FileText,
  DollarSign,
  Layers,
  ShieldCheck,
  Zap,
  Mail,
  Calendar,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import ROIResultsCapture from '@/components/ROIResultsCapture';

const CALENDLY = 'https://outlook.office.com/bookwithme/user/ca2e84f5d95b4280a34073c39ee722f3@ezcloud.ai/meetingtype/cGjhRG1JmkSrBrj2cFWWjA2?anonymous&ismsaljsauthenabled&ep=mlink';

const EZCLOUD_TO_YARDI = [
  { icon: FileText, label: 'Invoice metadata', note: 'Coded, approved, ready to post' },
  { icon: Layers, label: 'Intercompany metadata', note: 'Standardised across entities' },
  { icon: DollarSign, label: 'ACH payment data', note: 'Captured in EZ Cloud, posted to Yardi' },
  { icon: FileText, label: 'Linked invoice images', note: 'Attached to every transaction' },
];

const YARDI_TO_EZCLOUD = [
  { label: 'Expense accounts & descriptions' },
  { label: 'PO numbers & details' },
  { label: 'Property details & cash accounts' },
  { label: 'Active vendors & vendor codes' },
];

const DAY_ONE_CAPABILITIES = [
  {
    title: 'Route approvals by supplier',
    body: 'Auto-assign invoice approvers based on supplier name, with manual override. No more manually-routed invoices.',
  },
  {
    title: 'Handle construction insurance certificates',
    body: 'Separate flag for insurance certificates during data entry, stored at supplier level — never mis-filed as invoices.',
  },
  {
    title: 'Process intercompany invoices in one flow',
    body: 'Standardised intercompany processing across entities with real-time tracking between them.',
  },
  {
    title: 'Post ACH payments back to Yardi',
    body: 'ACH data captured by EZ Cloud transfers securely to Yardi, no re-keying, full audit trail.',
  },
];

const FAQS = [
  {
    q: 'Is EZ Cloud a native Yardi integration?',
    a: "Yes. The EZ Cloud Nexus Integration Layer connects to Yardi Voyager bi-directionally using Yardi's sanctioned integration interfaces. The integration is prebuilt and maintained by EZ Cloud — your team doesn't build a connector. Invoice metadata, intercompany records, ACH data, and linked images flow from EZ Cloud into Yardi; expense accounts, PO numbers, active vendors, and property details flow back.",
  },
  {
    q: 'Which Yardi product does EZ Cloud work with?',
    a: 'Yardi Voyager. If your team runs a different Yardi product (Breeze, Payscan add-ons), contact sales — we can confirm fit in a short scoping call.',
  },
  {
    q: 'How long does implementation take?',
    a: 'Typical Yardi implementations go live in 8-12 weeks depending on entity count, approval-workflow complexity, and the state of your current vendor data. The Housing Authority of Covington went live in 2021 and has expanded from 3 to 25+ active users since.',
  },
  {
    q: 'Do approvers need to log into Yardi?',
    a: "No. Approvers get an email notification, click through, review, and approve from a browser or mobile device. No Yardi license required for approvers. Denise Steffen at HACOV specifically called this out as a change that finally got approvers off paper.",
  },
  {
    q: 'How does EZ Cloud handle vendor credentialing and insurance certificates for construction contractors?',
    a: 'Supplemental attachments are stored at the supplier level. During data entry, users can flag an item as an insurance certificate so it\'s never mistaken for an invoice. Certificates are easily previewed, replaced, or deleted from the supplier page.',
  },
  {
    q: 'Is it SOC 2 compliant?',
    a: 'Yes. EZ Cloud operates in a SOC 2-compliant environment — important for public-sector housing authorities and regulated property managers.',
  },
];

const Yardi = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [roiInputs, setRoiInputs] = useState({ invoices: 1500, costPerInvoice: 14, staffRate: 30 });
  const annualSavings = useMemo(() => {
    const minutesSavedPerInvoice = 10;
    const hoursSaved = (roiInputs.invoices * minutesSavedPerInvoice) / 60;
    const laborMonthly = hoursSaved * roiInputs.staffRate;
    const processingMonthly = roiInputs.invoices * roiInputs.costPerInvoice * 0.5;
    return Math.max(0, (laborMonthly + processingMonthly) * 12);
  }, [roiInputs]);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Yardi Voyager AP Automation | Native Integration — EZ Cloud"
        description="Native Yardi Voyager AP automation — bi-directional sync through a prebuilt integration EZ Cloud builds and maintains. Auto-routing, intercompany workflows, insurance certificates, ACH. Trusted by HACOV."
        canonical="https://ezcloud.ai/integrations/yardi"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Integrations', url: 'https://ezcloud.ai/integrations' },
          { name: 'Yardi Voyager', url: 'https://ezcloud.ai/integrations/yardi' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      {/* Hero */}
      <section className="bg-hero-gradient text-white pt-16 pb-24 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent"></div>
          <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
        </div>

        <div className="relativecontainer mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
                <Network className="w-4 h-4" aria-hidden />
                Integration · Yardi Voyager
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Native Yardi Voyager AP automation.
              </h1>
              <p className="text-xl md:text-2xl text-white/80 mb-8">
                Bi-directional sync with Yardi through a prebuilt integration EZ Cloud builds and maintains — you don't build a connector. Invoices get coded, approved, and posted back into Yardi automatically.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  variant="accent"
                  className="font-semibold"
                  onClick={() => openCalendlyDemo()}
                >
                  <Calendar className="mr-2 w-5 h-5" aria-hidden />
                  See Yardi integration live
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
                </Button>
                <Button size="lg" variant="outline-on-dark" asChild>
                  <Link to="/customers/hacov">Read the HACOV case study</Link>
                </Button>
              </div>
            </div>

            {/* Data-flow visualization */}
            <Card className="p-6 bg-white/10 border-white/20 backdrop-blur-xl">
              <h2 className="text-white font-semibold mb-4">How the integration works</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-accent/20 rounded-lg border border-accent/30">
                  <div className="font-bold text-white">EZ Cloud</div>
                  <ArrowRight className="w-4 h-4 text-accent mx-auto" aria-hidden />
                  <div className="text-sm text-white/80 flex-1">Posts invoice metadata, intercompany data, ACH payments, invoice images</div>
                </div>
                <div className="text-center text-xs text-white/50 py-1">Nexus Integration Layer (bi-directional)</div>
                <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="font-bold text-white">Yardi Voyager</div>
                  <ArrowRight className="w-4 h-4 text-gray-600 mx-auto rotate-180" aria-hidden />
                  <div className="text-sm text-white/80 flex-1">Returns expense accounts, PO numbers, vendors, property details</div>
                </div>
              </div>
              <p className="text-xs text-white/50 mt-4">
                No staging database. Direct bi-directional sync. Integration built and maintained by EZ Cloud.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Data flow detail */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            What flows between EZ Cloud and Yardi
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Built from the EZ Cloud Nexus Integration Layer we deployed for the Housing Authority of Covington. Same connection, running since 2021.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">EZ Cloud → Yardi</h3>
              </div>
              <ul className="space-y-3">
                {EZCLOUD_TO_YARDI.map(({ icon: Icon, label, note }) => (
                  <li key={label} className="flex items-start gap-3">
                    <Icon className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" aria-hidden />
                    <div>
                      <div className="text-gray-900 font-medium">{label}</div>
                      <div className="text-sm text-gray-500">{note}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-primary rotate-180" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Yardi → EZ Cloud</h3>
              </div>
              <ul className="space-y-3">
                {YARDI_TO_EZCLOUD.map(({ label }) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="text-gray-900 font-medium">{label}</div>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-4">
                EZ Cloud reads Yardi's chart of accounts, PO data, vendor master, and property hierarchy in real time so coding decisions are always current.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Day-1 capabilities */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            What you can do on day one
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            No six-month implementation. The same integration running at the Housing Authority of Covington is what you get — with configuration tailored to your Yardi setup.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {DAY_ONE_CAPABILITIES.map((c) => (
              <Card key={c.title} className="p-5 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">{c.title}</h3>
                    <p className="text-sm text-gray-600">{c.body}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof: HACOV */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card className="p-8 bg-accent/10 border-accent/30">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <ShieldCheck className="w-4 h-4" aria-hidden />
              Customer · Housing Authority of Covington
            </div>
            <blockquote className="text-xl md:text-2xl text-gray-900 leading-relaxed italic mb-6">
              "The whole team was skeptical at first about how EZ Cloud and Yardi were going to work together, but they have — and it has been seamless. The team loves not having to physically sign approval on each invoice. Now they receive a notification, click the link, review, and approve. It's easy."
            </blockquote>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-gray-900 font-semibold">Denise Steffen</div>
                <div className="text-sm text-gray-600">Staff Accountant · HACOV</div>
              </div>
              <div className="flex gap-6 text-sm">
                <div>
                  <div className="text-2xl font-bold text-accent">71%</div>
                  <div className="text-gray-600">Faster</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-accent">80%</div>
                  <div className="text-gray-600">Fewer errors</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-accent">$79K+</div>
                  <div className="text-gray-600">Saved/yr</div>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Button variant="outline" asChild>
                <Link to="/customers/hacov">
                  Read the full case study
                  <ArrowRight className="ml-2 w-4 h-4" aria-hidden />
                </Link>
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Typical customer + ROI */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Typical EZ Cloud + Yardi customer</h2>
              <ul className="space-y-3 text-gray-600">
                {[
                  'Property management, housing authorities, real-estate operators',
                  'Running Yardi Voyager across multiple entities or properties',
                  '800–5,000 invoices per month',
                  'Heavy construction-contractor exposure (insurance certificates)',
                  'Mixed ACH and check payment mix moving toward ACH-first',
                ].map((x) => (
                  <li key={x} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link
                  to="/industries/property-management"
                  className="text-accent hover:underline inline-flex items-center gap-1"
                >
                  See our full Property Management solution
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </Link>
              </div>
            </Card>

            <Card className="p-6 bg-accent/10 border-accent/30">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Quick Yardi ROI estimate</h2>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Monthly invoice volume</label>
                  <input
                    type="number"
                    min={0}
                    value={roiInputs.invoices}
                    onChange={(e) => setRoiInputs({ ...roiInputs, invoices: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Cost per invoice ($)</label>
                  <input
                    type="number"
                    min={0}
                    value={roiInputs.costPerInvoice}
                    onChange={(e) =>
                      setRoiInputs({ ...roiInputs, costPerInvoice: Math.max(0, Number(e.target.value) || 0) })
                    }
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">AP staff rate ($/hr)</label>
                  <input
                    type="number"
                    min={0}
                    value={roiInputs.staffRate}
                    onChange={(e) =>
                      setRoiInputs({ ...roiInputs, staffRate: Math.max(0, Number(e.target.value) || 0) })
                    }
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900"
                  />
                </div>
              </div>
              <div className="mb-6">
                <div className="text-sm text-gray-700 mb-1">Estimated annual savings</div>
                <div className="text-4xl font-bold text-accent">
                  ${Math.round(annualSavings).toLocaleString()}
                </div>
              </div>
              <ROIResultsCapture
                industry="Yardi Integration"
                annualSavings={annualSavings}
                inputs={{
                  'Monthly invoices': roiInputs.invoices.toLocaleString(),
                  'Cost per invoice': `$${roiInputs.costPerInvoice}`,
                  'Staff hourly rate': `$${roiInputs.staffRate}`,
                }}
                buttonLabel="Email my Yardi ROI report"
              />
              <div className="mt-4 text-center">
                <Link
                  to="/resources/roi-calculator"
                  className="text-accent hover:underline inline-flex items-center gap-1 text-sm"
                >
                  Or use the full ROI calculator
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* RELATED RESOURCES */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                Related EZ Cloud resources
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Property management AP, the HACOV case study, and the integration technology behind Yardi-native invoice processing.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/customers/hacov"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  HACOV case study
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Housing Authority of Covington — 71% faster AP, 80% error reduction, $79K annual savings on Yardi Voyager.
                </p>
              </Link>

              <Link
                to="/industries/property-management"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Property management AP
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Multi-property GL coding, approval routing by property, month-end utility throughput — the full property-management AP solution.
                </p>
              </Link>

              <Link
                to="/customers"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Customer stories
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real outcomes from EZ Cloud customers — processing time, audit-readiness, multi-entity wins across Yardi and other ERPs.
                </p>
              </Link>

              <Link
                to="/technology/native-erp"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  How native ERP integration works
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real-time APIs, zero-duplication data flows, SOC 2 compliance — the architecture behind Yardi-native AP automation.
                </p>
              </Link>

              <Link
                to="/integrations"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  See all ERP integrations
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Yardi is one of five prebuilt EZ Cloud ERP integrations. See Oracle, NetSuite, SAP, and Dynamics too.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
              Yardi integration FAQ
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-gray-200 rounded-lg px-6 py-2 shadow-sm hover:shadow-md transition-shadow bg-white"
                >
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline hover:text-primary">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to see EZ Cloud running inside your Yardi?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            30-minute live demo — we'll walk your chart of accounts, your approval rules, and a sample invoice flow.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              variant="accent"
              onClick={() => openCalendlyDemo()}
            >
              Book a Yardi demo
              <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="mailto:hello@ezcloud.ai?subject=Yardi%20integration%20question">
                <Mail className="mr-2 w-5 h-5" aria-hidden />
                Email hello@ezcloud.ai
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Yardi;
